import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Platform,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../screens/ThemeContext';

const TermsSection = ({ title, children, theme }) => (
  <View style={styles.section}>
    <Text style={[styles.sectionTitle, { color: theme.text }]}>{title}</Text>
    <View style={[styles.sectionContent, { backgroundColor: theme.surface }]}>
      {children}
    </View>
  </View>
);

const TermsItem = ({ content, theme }) => (
  <Text style={[styles.termText, { color: theme.textSecondary }]}>{content}</Text>
);

const TermsOfServiceScreen = ({ navigation }) => {
  const { theme } = useTheme();

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: theme.background }]}>
      {Platform.OS === 'android' && <StatusBar barStyle={theme.statusBar} backgroundColor={theme.background} />}

      {/* Header */}
      <View style={[styles.header, { backgroundColor: theme.background }]}>
        <TouchableOpacity 
            onPress={() => navigation.goBack()}
            style={[styles.backButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
          >
            <MaterialCommunityIcons 
              name="arrow-left" 
              size={24} 
              color={theme.primary} 
            />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: theme.text }]}>
        Conditions d'utilisation
        </Text>
      </View>

      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        <View style={styles.content}>
          {/* Introduction */}
          <TermsSection title="Introduction" theme={theme}>
            <TermsItem
              theme={theme}
              content="En utilisant l'application MyConcours, vous acceptez d'être lié par les présentes conditions d'utilisation. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser l'application."
            />
          </TermsSection>

          {/* Utilisation du Service */}
          <TermsSection title="Utilisation du Service" theme={theme}>
            <TermsItem
              theme={theme}
              content="• Vous devez créer un compte pour accéder à certaines fonctionnalités de l'application."
            />
            <TermsItem
              theme={theme}
              content="• Vous êtes responsable de maintenir la confidentialité de vos informations de connexion."
            />
            <TermsItem
              theme={theme}
              content="• Vous acceptez de ne pas utiliser le service à des fins illégales ou non autorisées."
            />
            <TermsItem
              theme={theme}
              content="• L'accès à certains contenus peut nécessiter un abonnement payant."
            />
          </TermsSection>

          {/* Contenu et Propriété Intellectuelle */}
          <TermsSection title="Contenu et Propriété Intellectuelle" theme={theme}>
            <TermsItem
              theme={theme}
              content="• Tout le contenu disponible sur MyConcours est protégé par des droits d'auteur."
            />
            <TermsItem
              theme={theme}
              content="• Les documents téléchargés sont pour un usage personnel uniquement."
            />
            <TermsItem
              theme={theme}
              content="• La redistribution ou la revente du contenu est strictement interdite."
            />
          </TermsSection>

          {/* Abonnements et Paiements */}
          {/* <TermsSection title="Abonnements et Paiements" theme={theme}>
            <TermsItem
              theme={theme}
              content="• Les paiements sont traités de manière sécurisée via CAMPAY."
            />
            <TermsItem
              theme={theme}
              content="• Les abonnements se renouvellent automatiquement sauf annulation."
            />
            <TermsItem
              theme={theme}
              content="• Les remboursements sont soumis à notre politique de remboursement."
            />
          </TermsSection> */}

          {/* Centres de Préparation */}
          <TermsSection title="Centres de Préparation" theme={theme}>
            <TermsItem
              theme={theme}
              content="• Les centres sont responsables des informations qu'ils publient."
            />
            <TermsItem
              theme={theme}
              content="• MyConcours n'est pas responsable des services fournis par les centres."
            />
            <TermsItem
              theme={theme}
              content="• Les transactions avec les centres sont régies par leurs propres conditions."
            />
          </TermsSection>

          {/* Confidentialité */}
          <TermsSection title="Confidentialité" theme={theme}>
            <TermsItem
              theme={theme}
              content="• Nous collectons et utilisons vos données conformément à notre politique de confidentialité."
            />
            <TermsItem
              theme={theme}
              content="• Vos informations personnelles sont protégées et ne seront pas vendues à des tiers."
            />
          </TermsSection>

          {/* Résiliation */}
          <TermsSection title="Résiliation" theme={theme}>
            <TermsItem
              theme={theme}
              content="• Nous nous réservons le droit de suspendre ou résilier votre compte en cas de violation des conditions."
            />
            <TermsItem
              theme={theme}
              content="• Vous pouvez résilier votre compte à tout moment depuis les paramètres."
            />
          </TermsSection>

          {/* Modifications des Conditions */}
          <TermsSection title="Modifications des Conditions" theme={theme}>
            <TermsItem
              theme={theme}
              content="• Nous pouvons modifier ces conditions à tout moment."
            />
            <TermsItem
              theme={theme}
              content="• Les modifications importantes seront notifiées aux utilisateurs."
            />
            <TermsItem
              theme={theme}
              content="• L'utilisation continue de l'application après modification constitue une acceptation des nouvelles conditions."
            />
          </TermsSection>

          {/* Contact */}
          <TermsSection title="Contact" theme={theme}>
            <TermsItem
              theme={theme}
              content="Pour toute question concernant ces conditions d'utilisation, contactez-nous à jobs@cameroondesks.com"
            />
          </TermsSection>

          {/* Date de mise à jour */}
          <Text style={[styles.updateDate, { color: theme.textSecondary }]}>
            Dernière mise à jour : Mai 2024
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
  },
  backButton: {
    padding: 8,
    marginRight: 16,
    borderRadius: 12,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  container: {
    flex: 1,
  },
  content: {
    padding: 20,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  sectionContent: {
    padding: 15,
    borderRadius: 12,
  },
  termText: {
    fontSize: 14,
    lineHeight: 22,
    marginBottom: 10,
  },
  updateDate: {
    textAlign: 'center',
    fontSize: 14,
    marginTop: 20,
    marginBottom: 40,
  },
});

export default TermsOfServiceScreen;